import { useEffect, useState } from 'react';
import axios from 'axios';
import { contractorAuth, portalContractor } from './contractorAuth';

/**
 * What every asset screen needs to know before it can draw itself.
 *
 * GET /assets/meta answers in one call: whether the register is switched on,
 * what this person may do in it, and the categories and places. Asked once
 * and shared, the way lib/me.js shares who you are, because the navbar, the
 * list, the device page and the ticket form all need the same answer and
 * four requests for it would let them disagree for a moment.
 *
 * A 503 means the register's migrations have not run on this database. That
 * comes back as { missing: true } rather than as an error, so an
 * administrator sees "run migrations 039 to 041" instead of a broken page,
 * and everybody else simply sees no Assets item at all.
 *
 * Like lib/me.js this only decides what is SHOWN. The server checks every
 * request regardless.
 */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

let cached = null;
let inflight = null;
const listeners = new Set();

export function fetchAssetMeta({ force = false } = {}) {
  if (!force && cached) return Promise.resolve(cached);
  if (!force && inflight) return inflight;
  // A contractor with a grant (062) reaches the register on their portal
  // token; for a member of staff contractorAuth() is undefined and the
  // app's own interceptor signs the call as before.
  inflight = axios.get(`${API_BASE}/assets/meta`, contractorAuth())
    .then((r) => r.data)
    .catch((e) => (e.response?.status === 503
      ? { missing: true, error: e.response?.data?.error }
      : { failed: true }))
    .then((meta) => {
      cached = meta;
      inflight = null;
      listeners.forEach((fn) => fn(meta));
      return meta;
    });
  return inflight;
}

export function useAssetMeta(enabled = true) {
  const [meta, setMeta] = useState(cached);
  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    listeners.add(setMeta);
    fetchAssetMeta().then((m) => { if (live) setMeta(m); });
    return () => { live = false; listeners.delete(setMeta); };
  }, [enabled]);
  return meta;
}

/** After a change in Settings: ask again, and tell everybody listening. */
export const refreshAssetMeta = () => fetchAssetMeta({ force: true });

/**
 * The axios config for GET /assets/mine on the contractor's side: their own
 * portal token, or - for an administrator looking at a contractor's portal
 * through View as - the contractor named, which the server honours for an
 * administrator and nobody else. Undefined when no portal is on screen, so a
 * member of staff's request goes through the interceptor as it always has.
 */
export function myEquipmentRequest() {
  const portal = portalContractor();
  if (!portal) return undefined;
  if (!portal.viewing) return contractorAuth();
  return { params: { contractor_id: portal.id } };
}

export const LIFECYCLE_LABEL = {
  ordered: 'Ordered',
  received: 'Received',
  in_stock: 'In stock',
  deployed: 'In use',
  in_repair: 'In repair',
  retired: 'Retired',
  disposed: 'Disposed'
};

export const OPERATIONAL_LABEL = {
  operational: 'Working',
  degraded: 'Degraded',
  faulty: 'Faulty',
  unknown: 'Unknown'
};

export const CONDITION_LABEL = {
  new: 'New',
  good: 'Good',
  fair: 'Fair',
  damaged: 'Damaged',
  beyond_repair: 'Beyond repair'
};

export const OWNERSHIP_LABEL = {
  owned: 'Owned', leased: 'Leased', rented: 'Rented', byod: 'Personal (BYOD)'
};

/** The semantic tone a status carries: the same word, the same colour. */
export const lifecycleTone = (s) => ({
  deployed: 'success', in_stock: 'info', in_repair: 'warning',
  ordered: 'idle', received: 'idle', retired: 'idle', disposed: 'idle'
}[s] || 'idle');

export const operationalTone = (s) => ({
  operational: 'success', degraded: 'warning', faulty: 'critical'
}[s] || 'idle');

const DAY = 24 * 60 * 60 * 1000;
/*
 * A calendar date, read as the local day it names. Date.parse('2027-03-03')
 * is midnight UTC, which west of Greenwich is the evening before - so the
 * parts are read directly, and the server sends plain YYYY-MM-DD for exactly
 * this reason (lib/asset-card.js dayOf).
 */
const parseDay = (d) => {
  if (!d) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(d));
  if (!m) return null;
  const t = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(t.getTime()) ? null : t;
};

export const formatDay = (d) => {
  const t = parseDay(d);
  return t ? t.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
};

const spanInWords = (days) => {
  const d = Math.abs(days);
  if (d < 45) return `${d} day${d === 1 ? '' : 's'}`;
  const months = Math.round(d / 30.44);
  if (months < 24) return `${months} month${months === 1 ? '' : 's'}`;
  const years = Math.round(d / 365.25);
  return `${years} year${years === 1 ? '' : 's'}`;
};

/**
 * The warranty position as a sentence, which is how the spec asks for it:
 * "Under warranty until 3 March 2027", "Warranty expired 14 months ago",
 * "No warranty recorded". `tone` says how worried to look.
 */
export function warrantyInWords(end, now = new Date()) {
  const e = parseDay(end);
  if (!e) return { text: 'No warranty recorded', tone: 'idle' };
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.round((e - today) / DAY);
  if (days < 0) return { text: `Warranty expired ${spanInWords(days)} ago`, tone: 'critical' };
  if (days === 0) return { text: 'Warranty ends today', tone: 'warning' };
  if (days <= 60) {
    return { text: `Under warranty until ${formatDay(end)} — ${spanInWords(days)} left`, tone: 'warning' };
  }
  return { text: `Under warranty until ${formatDay(end)}`, tone: 'success' };
}

/**
 * The nine colours a company (062) can carry - the same names the to-do
 * lists use, drawn from the same --color-swatch-* tokens, so one picker and
 * one set of chips serve both.
 */
export const COMPANY_COLORS = ['red', 'orange', 'yellow', 'green', 'teal', 'blue', 'purple', 'pink', 'grey'];

/**
 * The company filter the list remembers per browser: which companies were
 * ticked last time, so IT looking after one company's devices does not
 * re-tick it on every visit. Never sensitive: a few ids.
 */
const COMPANY_FILTER_KEY = 'as_company_filter';
export function rememberedCompanies() {
  try {
    const v = JSON.parse(localStorage.getItem(COMPANY_FILTER_KEY) || '[]');
    return Array.isArray(v) ? v.map(String) : [];
  } catch { return []; }
}
export function rememberCompanies(ids) {
  try { localStorage.setItem(COMPANY_FILTER_KEY, JSON.stringify(ids || [])); } catch { /* private window */ }
}

/** "Dell Latitude 5440", or the category when nothing more is known. */
export const deviceName = (a) =>
  [a?.manufacturer, a?.model].filter(Boolean).join(' ') || a?.category_name || 'Device';

/**
 * WHO HOLDS IT, in words. Since migration 093 a device may be with a
 * contractor rather than a member of staff (lib/asset-holder.js on the
 * server), and the row says which in holder_kind. A person is their name;
 * a contractor is "Marco Vella (contractor)", or "Marco Vella (contractor ·
 * Fixit Ltd)" when their company is known - so every screen that says
 * "With …" says the same thing. Null when nobody has it.
 */
export function holderLabel(a) {
  if (!a?.holder_name) return a?.desk_id && a?.desk_name ? deskLabel(a) : null;
  if (a.holder_kind !== 'contractor') return a.holder_name;
  return `${a.holder_name} (contractor${a.holder_company ? ` · ${a.holder_company}` : ''})`;
}

/**
 * A DESK AS A HOLDER (108): a device at a desk with nobody holding it is
 * the desk's kit, and reads "Desk 2.14 (Rita Gatt’s desk)", "Desk 2.15
 * (hot desk)" or "Desk 2.16 (unassigned)" - lib/desks.js label() on the
 * server says the same. Takes a record (desk_name, desk_mode,
 * desk_user_name) or a desk (name, desk_mode, desk_user_name).
 */
export function deskLabel(d) {
  const name = d?.desk_name || d?.name;
  if (!name) return null;
  if (d.desk_mode === 'hot') return `${name} (hot desk)`;
  if (d.desk_user_name) return `${name} (${d.desk_user_name}’s desk)`;
  return `${name} (unassigned)`;
}

/**
 * A holder as the API names one: 'u:<id>' for a person, 'c:<id>' for a
 * contractor - the same keys instruction recipients and participants use.
 */
export const holderKeyOf = (a) => (a?.assigned_contractor_id ? `c:${a.assigned_contractor_id}`
  : a?.assigned_user_id ? `u:${a.assigned_user_id}` : '');

/** The key split back into the field the server reads, or {} for nobody. */
export function holderFields(key, { action = false } = {}) {
  const m = /^([uc]):(.+)$/.exec(key || '');
  if (!m) return {};
  if (m[1] === 'c') return action ? { contractor_id: m[2] } : { assigned_contractor_id: m[2] };
  return action ? { user_id: m[2] } : { assigned_user_id: m[2] };
}

/**
 * Download a file from the API as the signed-in person.
 *
 * A plain link cannot carry the sign-in header, so the file is fetched
 * through axios - which the app's interceptor signs - and handed to the
 * browser as a temporary object URL. The name comes from the server's
 * Content-Disposition when it sends one.
 */
export async function downloadFile(path, fallbackName) {
  const r = await axios.get(`${API_BASE}${path}`, { responseType: 'blob', ...(contractorAuth() || {}) });
  const disposition = r.headers?.['content-disposition'] || '';
  const named = /filename\*=UTF-8''([^;]+)/i.exec(disposition) || /filename="([^"]+)"/i.exec(disposition);
  const name = named ? decodeURIComponent(named[1]) : fallbackName;
  const url = URL.createObjectURL(r.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Label stock, in millimetres, for the print screen: the sizes most sold in
 * Europe, so IT can buy whichever they find. Avery sheets are A4; the two
 * label-printer rolls print one label per page.
 */
export const LABEL_STOCK = [
  { id: 'L7651', name: 'Avery L7651 — 65 per sheet, 38.1 × 21.2 mm (laptops)',
    width: 38.1, height: 21.2, columns: 5, rows: 13, top: 10.7, left: 4.7, gap_x: 2.5, gap_y: 0 },
  { id: 'L7160', name: 'Avery L7160 — 21 per sheet, 63.5 × 38.1 mm (screens, printers)',
    width: 63.5, height: 38.1, columns: 3, rows: 7, top: 15.1, left: 7.2, gap_x: 2.5, gap_y: 0 },
  { id: 'L7163', name: 'Avery L7163 — 14 per sheet, 99.1 × 38.1 mm (large equipment)',
    width: 99.1, height: 38.1, columns: 2, rows: 7, top: 15.1, left: 4.7, gap_x: 2.5, gap_y: 0 },
  { id: 'DK11201', name: 'Brother DK-11201 roll — 29 × 90 mm', roll: true,
    width: 90, height: 29, columns: 1, rows: 1, top: 0, left: 0, gap_x: 0, gap_y: 0 },
  { id: 'DYMO11354', name: 'Dymo 11354 roll — 57 × 32 mm', roll: true,
    width: 57, height: 32, columns: 1, rows: 1, top: 0, left: 0, gap_x: 0, gap_y: 0 }
];

/** The tag in a scanned code: the end of a /a/<tag> address, or the text itself. */
export function tagFromScan(text) {
  const raw = String(text || '').trim();
  const m = /\/a\/([^/?#\s]+)\s*$/.exec(raw);
  try { return decodeURIComponent(m ? m[1] : raw); } catch { return m ? m[1] : raw; }
}

/**
 * A code nothing in the register matched, read the way the server reads it
 * (backend lib/asset-code.js): 'url' for a web address (a Jarvis label or
 * somebody else's), 'mac' for twelve hex digits, 'tag' for TAG-LIKE-0001,
 * otherwise 'serial'. For a code the server did not say (the stock take's
 * unknown list).
 */
export function codeKindOf(raw) {
  const code = String(raw || '').trim();
  if (/\/a\/[^/?#\s]+\/?(?:[?#].*)?$/i.test(code) || /^[a-z][a-z0-9+.-]*:\/\//i.test(code)) return 'url';
  if (macOf(code)) return 'mac';
  if (/^[A-Z]{2,4}-[A-Z0-9]{2,6}-\d+$/i.test(code)) return 'tag';
  return 'serial';
}

/** aa:bb:cc:dd:ee:ff, or null: exactly as the server normalises a MAC (backend lib/oui.js). */
function macOf(raw) {
  const hex = String(raw || '').replace(/[^0-9a-fA-F]/g, '').toLowerCase();
  if (hex.length !== 12 || /^0{12}$/.test(hex) || /^f{12}$/.test(hex)) return null;
  return hex.match(/.{2}/g).join(':');
}

/**
 * What the add form starts with for a scanned code nothing matched (Gordon,
 * 4 November): a tag as the tag, a serial number as the serial, a MAC
 * address as the MAC, and a web address as nothing. `kind` is the server's
 * code_kind where it gave one.
 */
export function prefillFromCode(raw, kind = null) {
  const code = String(raw || '').trim();
  if (!code) return {};
  const k = kind || codeKindOf(code);
  if (k === 'tag') return { asset_tag: code };
  if (k === 'serial') return { serial_number: code };
  if (k === 'mac') return { mac_address: macOf(code) || code };
  return {};
}

/** The tag as text; a device without one (personal, or not yet kept) reads as its make and model. */
export const tagText = (a) => a?.asset_tag || deviceName(a);

/** What stands where the tag would: Personal, or "No tag yet" for a company device not yet kept. */
export const noTagLabel = (a) => (a?.is_personal ? 'Personal' : 'No tag yet');
